import {inject, Injectable} from '@angular/core';
import {Apollo} from "apollo-angular";
import {
  call_single_participant,
  delete_permission,
  delete_pipeline_filter,
  get_base_pipeline_views, get_filtered_pipeline, get_filtered_tags,
  get_group_list,
  get_pipeline_field_filters,
  get_pipeline_view_filters,
  get_selections_by_model, get_single_pipeline, get_sub_filters, save_pipeline_filter,
  share_pipeline_filter, tagged_participants_by_project, update_pipeline_filter,
} from "./graphql/getPipelines";
import {BehaviorSubject, Observable, of, tap} from "rxjs";
import PipelineViewFilter from "../interfaces/pipeline-view-filter";
import PipelineFieldFilter from "../interfaces/pipeline-field-filter";
import StatusSequenceDetail from "../interfaces/status-sequence-detail";
import StatusSequence from "../interfaces/status-sequence";
import State from "../interfaces/state";
import {FormControl, FormGroup} from "@angular/forms";
import ProjectCreditOutlet from "../interfaces/project-credit-outlet";
import TaxCreditType from "../interfaces/tax-credit-type";
import {participants_search_people} from "../deals/deals-subcomponents/participants/graphql/participantsSearchPeople";
import {map} from "rxjs/operators";
import Group from "../interfaces/group";
import PipelineView from "../interfaces/pipeline-view";
import PipelineField from "../interfaces/pipeline-field";
import {
  PipelinesSortBlockComponent
} from "./pipelines-subcomponents/pipelines-filter-modal/pipelines-sort-block/pipelines-sort-block.component";
import {
  PipelinesConditionBlockComponent
} from "./pipelines-subcomponents/pipelines-filter-modal/pipelines-condition-block/pipelines-condition-block.component";
import {HttpClient, HttpHeaders} from "@angular/common/http";
import {EnvironmentService} from "../environment/environment.service";
import {AuthService} from "../auth/auth.service";
import UserPipelinePerm from "../interfaces/user-pipeline-permission";
import Participant from "../interfaces/participant";
import Organization from "../interfaces/organization";
import {Person} from "../interfaces/person";
import AttributeType from "../interfaces/attribute-type";
import DateType from "../interfaces/date-type";
import Role from "../interfaces/role";
import Milestone from "../interfaces/milestone";
import {PipelineMilestone} from "../interfaces/pipeline-milestone";

export interface CachedPipelineDataStore {
  pipeline_view_id: string | null,
  pipeline_view_filter_id: string | null,
  filter: string|any[],
  sort: string|any[],
  group: string|any[]
}
interface ModelSelection {
  id: string,
  name: string,
  index: number
}

interface PipelineSection {
  name: string,
  projects: any[],
  index: number,
  totals: any[]
}

interface PipelineTotal {
  name: string,
  format: string | null,
  value: number,
  count: number | null,
  equity: number | null
}
export interface PipelineExportInput{
  filter?:any[],
  columns?:any[],
  sort?:any[],
  group?:any[],
  pipeline_view_id?:string|null,
  pipeline_view_filter_id?:string|null,
}
@Injectable({
  providedIn: 'root',
})
export class PipelinesService {

  private _fieldFilters:PipelineFieldFilter[] = []
  private fieldFilters = new BehaviorSubject(this._fieldFilters);
  public fieldFilters$ = this.fieldFilters.asObservable()

  private _sortFilters:PipelineFieldFilter[] = []
  private sortFilters = new BehaviorSubject(this._sortFilters)
  public sortFilters$ = this.sortFilters.asObservable();

  private _groupFilters:PipelineFieldFilter[] = []
  private groupFilters = new BehaviorSubject(this._groupFilters)
  public groupFilters$ = this.groupFilters.asObservable()

  private _exportFilters:PipelineFieldFilter[] = []
  private exportFilters = new BehaviorSubject(this._exportFilters)
  public exportFilters$ = this.exportFilters.asObservable();


  private _baseViews:PipelineView[] = []
  private baseViews = new BehaviorSubject(this._baseViews)
  public baseViews$ = this.baseViews.asObservable();

  private _fields:PipelineField[] = []
  private fields = new BehaviorSubject(this._fields)
  public fields$ = this.fields.asObservable();

  private _sections:PipelineSection[] = []
  private sections = new BehaviorSubject(this._sections)
  public sections$ = this.sections.asObservable()

  private _topTotals:PipelineTotal[] = []
  private topTotals = new BehaviorSubject(this._topTotals)
  public topTotals$ = this.topTotals.asObservable();

  private _viewFilters:PipelineViewFilter[]= []
  private viewFilters = new BehaviorSubject(this._viewFilters)
  public viewFilters$ = this.viewFilters.asObservable();

  private _globalViewFilters: PipelineViewFilter[] = []
  private globalViewFilters = new BehaviorSubject(this._globalViewFilters)
  public globalViewFilters$ = this.globalViewFilters.asObservable()

  private _ownedViewFilters: PipelineViewFilter[] = []
  private ownedViewFilters = new BehaviorSubject(this._ownedViewFilters)
  public ownedViewFilters$ = this.ownedViewFilters.asObservable()

  private _combinedViewFilters: PipelineViewFilter[] = []
  private combinedViewFilters = new BehaviorSubject(this._combinedViewFilters)
  public combinedViewFilters$ = this.combinedViewFilters.asObservable()

  private _sharedViewFilters: UserPipelinePerm[] = []
  private sharedViewFilters = new BehaviorSubject(this._sharedViewFilters)
  public sharedViewFilters$ = this.sharedViewFilters.asObservable()

  private _investorViewFilters: UserPipelinePerm[] = []
  private investorViewFilters = new BehaviorSubject(this._investorViewFilters)
  public investorViewFilters$ = this.investorViewFilters.asObservable();

  private _modelSelections:Map<string,any[]> = new Map<string, any[]> ()
  private modelSelections = new BehaviorSubject<Map<string,any[]>>(this._modelSelections)
  public modelSelections$ = this.modelSelections.asObservable();

  private _conditionSelections:WeakMap<FormGroup,any[]> = new WeakMap<FormGroup, any[]>()

  private _cachedPipelineData: CachedPipelineDataStore | null = null
  private cachedPipelineData = new BehaviorSubject(this._cachedPipelineData)
  public cachedPipelineData$ = this.cachedPipelineData.asObservable()

  private _pipelineName: string | null = null;
  private pipelineName = new BehaviorSubject<string | null>(this._pipelineName)
  public pipelineName$ = this.pipelineName.asObservable()

  // private _modelSelections:ModelSelection[] = []
  // private modelSelections = new BehaviorSubject(this._modelSelections)
  // public modelSelections$ = this.modelSelections.asObservable();

  private _groupingSelections: ModelSelection[] = []
  private groupingSelections = new BehaviorSubject(this._groupingSelections)
  public groupingSelections$ = this.groupingSelections.asObservable()

  private _sortingSelections: Map<string,any[]> = new Map<string,any[]>()
  private sortingSelections = new BehaviorSubject(this._sortingSelections)
  public sortingSelections$ = this.sortingSelections.asObservable()

  private _currentUser: any = {}
  private currentUser = new BehaviorSubject(this._currentUser)
  public currentUser$ = this.currentUser.asObservable();

  private _projectsTags: any = {}
  private projectsTags = new BehaviorSubject(this._projectsTags)
  public projectsTags$ = this.projectsTags.asObservable()


  private _currentUserGroupIds: string[] = []
  private currentUserGroupIds = new BehaviorSubject(this._currentUserGroupIds)
  public currentUserGroupIds$ = this.currentUserGroupIds.asObservable();

  private _currentBaseView: PipelineView |null = null;
  private currentBaseView= new BehaviorSubject(this._currentBaseView);
  public currentBaseView$ = this.currentBaseView.asObservable();


  public rawData:any[] = []
  private _rawDataByModel:Map<string,any[]> = new Map<string,any[]>()

  private _loading = false;
  private loading = new BehaviorSubject(this._loading);
  public loading$ = this.loading.asObservable();

  private _setFilters(filters:PipelineFieldFilter[]){
    this._fieldFilters = filters.filter((field: PipelineFieldFilter) => field.is_filterable);
    this._fieldFilters.sort((a: PipelineFieldFilter, b:PipelineFieldFilter) => a.name.localeCompare(b.name))
    this.fieldFilters.next(this._fieldFilters)
    this._groupFilters = filters.filter((field: PipelineFieldFilter) => field.is_grouped);
    this._groupFilters.sort((a: PipelineFieldFilter, b:PipelineFieldFilter) => a.name.localeCompare(b.name))
    this.groupFilters.next(this._groupFilters)
    this._sortFilters = filters.filter((field: PipelineFieldFilter) => field.is_sortable)
    this._sortFilters.sort((a: PipelineFieldFilter, b: PipelineFieldFilter) => a.name.localeCompare(b.name))
    this.sortFilters.next(this._sortFilters)
    this._exportFilters = filters.filter((field: PipelineFieldFilter) => field.is_exportable)
    this._exportFilters.sort((a: PipelineFieldFilter, b: PipelineFieldFilter) => a.name.localeCompare(b.name))
    this.exportFilters.next(this._exportFilters)
  }

  private _setLoading(loading:boolean){
    this._loading = loading;
    this.loading.next(this._loading)
  }
  private _setFields(fields:any[]){
    this._fields = fields;
    this.fields.next(this._fields)
  }

  private _setViewFilters(filters:PipelineViewFilter[]){
    this._viewFilters = filters;
    this.viewFilters.next(this._viewFilters)
  }

  public parseField(field:string){
    return JSON.parse(field)
  }


  constructor(private apollo: Apollo, private http: HttpClient, private env: EnvironmentService, private auth: AuthService) {
  }

  public getPipelineFields(){
    this.apollo.query({
      query: get_pipeline_field_filters
    }).subscribe((response:any) => {
      let projdate = response.data.pipelineFieldFilters
      this._setFilters(response.data.pipelineFieldFilters)
      //this._setFields(response.data.pipelineFields)
    })
  }

  public getPipelineViews(){
    this.apollo.query({
      fetchPolicy: 'network-only',
      query: get_pipeline_view_filters
    }).subscribe((response:any) => {
      this._combinedViewFilters = [...response.data.owned, ...response.data.global]
      this.combinedViewFilters.next(this._combinedViewFilters)
      let owned: PipelineViewFilter[] = this._combinedViewFilters.filter((filter: PipelineViewFilter) => {
        let validGroups = response.data.me.user_profile.user.groupsFlattened.filter((grp:Group) => grp.id === filter.group_id)
        if(filter.person_id === response.data.me.user_profile.user.person.id || validGroups.length){
          return filter;
        }
        return null;
      })
      let shared: any[] = response.data.me.user_profile.user.permissionPipelines.filter((perm: UserPipelinePerm) => perm.pipeline.associatedOrganization==null && perm.pipeline.associatedSponsor==null)
      let investorFilters:any[] = response.data.me.user_profile.user.permissionPipelines.filter((perm:UserPipelinePerm) => perm.pipeline.associatedOrganization!==null || perm.pipeline.associatedSponsor!==null)

      this._sharedViewFilters = shared
      this.sharedViewFilters.next(this._sharedViewFilters)

      this._investorViewFilters = investorFilters;
      this.investorViewFilters.next(this._investorViewFilters)

      this._currentUser = response.data.me
      this.currentUser.next(this._currentUser)
      this._currentUserGroupIds = [response.data.me.user_profile.user.person.id]

      response.data.me.user_profile.user.groups.forEach((group:Group) => this._currentUserGroupIds.push(group.id as string))
      this.currentUserGroupIds.next(this._currentUserGroupIds)

      this._ownedViewFilters = owned;
      this.ownedViewFilters.next(this._ownedViewFilters)
      this._globalViewFilters = response.data.global;
      this.globalViewFilters.next(this._globalViewFilters)
      let views:PipelineViewFilter[] = [...response.data.global, ...response.data.owned]
      this._setViewFilters(views)
    })
  }

  public getBaseViews(){
    this.apollo.query({
      query: get_base_pipeline_views
    }).subscribe((response: any) => {
      let data: PipelineView[] = response.data.getBaseViews
      this._baseViews = data
      this.baseViews.next(this._baseViews)
    })
  }

  public getFilteredPipeline(data:any){
    this._cachedPipelineData = data;
    this.cachedPipelineData.next(this._cachedPipelineData)
    this._setLoading(true)
    return this.apollo.query({
      fetchPolicy: 'network-only',
      query: get_filtered_pipeline,
      variables: {
        pipeline_view_filter_id: data.pipeline_view_filter_id ?? null,
        pipeline_view_id: data.pipeline_view_id ?? null,
        filter: data.filter ? JSON.stringify(data.filter) : null,
        sort: data.sort ? JSON.stringify(data.sort) : null,
        group: data.group ? JSON.stringify(data.group) : null
      }
    }).pipe(
      tap((response:any) => {
        this._setLoading(false);
        let formattedData: any = this.parseField(response.data.getFilteredPipeline)
        if(formattedData){
          this._pipelineName = formattedData['name']
          this.pipelineName.next(this._pipelineName)
          this._fields = formattedData['fields']
          this.fields.next(this._fields)
          this._sections = formattedData['sections']
          this.sections.next(this._sections)
          this._topTotals = formattedData['totals']
          this.topTotals.next(this._topTotals)
          this._setCurrentBaseView(formattedData['pipeline_view_id']);
        }
      })
    )
  }

  public refreshFilteredPipeline(){
    if(this._cachedPipelineData){
      return this.getFilteredPipeline(this._cachedPipelineData)
    }
    return of(null)
  }

  public getModelLookup(model:string, field: string | null, group?: boolean, sort?:boolean){
    let variables:any = {}
    switch (model) {
      case 'StatusSequence' : variables = {model: model, with: ['status_sequence_details.status']};
        break;
      case 'Subsequence' : variables = {model: model, with: ['substatuses']};
        break;
      case 'AttributeType': variables = {model:model, with: ['attributes']};
        break;
      case 'State' : variables = {model:model, with: ['counties']}
        break;
      case 'ProjectCreditOutlet': variables = {model: model, with: ['fund', 'certificatedOutlet.organization']}
        break;
      default: variables = {model:model, with: []}
        break;
    }

    this.apollo.query({
      fetchPolicy: 'network-only',
      query: get_selections_by_model,
      variables: variables
    }).subscribe((response:any) => {
      let data = response.data.pipelineModelFieldSelection
      let parsedData = this.parseField(data)
      if(group){
        this._groupingSelections = [...this._formatModelSelectionData(parsedData, model, field)]
        this.groupingSelections.next(this._groupingSelections)
      } else if (sort){
        let activeData = parsedData.filter((item: any) => !item.hasOwnProperty('active') || (item.active !== false && item.active !== '0'))
        this._sortingSelections.set(model,[...this._formatModelSelectionData(activeData, model, field )])
        this.sortingSelections.next(this._sortingSelections)
      } else {
        this.rawData = parsedData
        this._rawDataByModel.set(model, parsedData)
        if(parsedData!==null){
          this._modelSelections.set(model, [...this._formatModelSelectionData(parsedData, model, field)])
          this.modelSelections.next(this._modelSelections)
        }
      }
    })
  }

  public sharePipelineFilter(id: string,data:any, group_id: any, person_id: any){
    return this.apollo.mutate({
      mutation: share_pipeline_filter,
      variables: {
        id: id,
        input: data,
        group_id: group_id,
        person_id: person_id
      }
    })
  }


  public searchModel(model:string, string: string): Observable<any[]>{
    if(model==="Participant"){
      return this.apollo.query({
        query: participants_search_people,
        variables: {
          search: string,
          searchLike: '%' + string + '%',
          role: null
        },
        fetchPolicy: "network-only"
      }).pipe(
        map((response) => {
          const data:any = response.data
          return [...data.participant_orgs.data, ...data.participant_sponsors.data, ...data.participant_people.data]
        })
      )
    }
    return of(this._modelSelections.get(model)?.filter((selection:ModelSelection) => selection.name !==null && selection.name.toLowerCase().includes(string.toLowerCase())) ?? [])
  }

  public getPermissionGroups(): Observable<any> {
    return this.apollo.query({
      query: get_group_list
    }).pipe(
      map((response: any) => {
        return response.data
        //return response.data.getGroups.filter((group: Group) => group.groupType?.name.toLowerCase() === 'department')
      })
    )
  }

  private _getFilterTags(ids:string[]){
    this.apollo.query({
      query: get_filtered_tags,
      variables: {ids: ids}
    }).subscribe((result:any) => {
      let parseData = this.parseField(result.data.projectTags)
      Object.keys(parseData).forEach((key) => {
        if(!Array.isArray(parseData[key])){
          let ob = Object.values(parseData[key])[0]
          this._projectsTags[key] = ob
        }
      })
      this.projectsTags.next(this._projectsTags)
    })
  }

  public getPipelineSingle(id:string){
    return this.apollo.query({
      query: get_single_pipeline,
      variables: {id}
    })
  }

  public getExportModalColumns(){
    return this.apollo.query({
      query: get_sub_filters
    }).pipe(
      map((response:any) => {
        return response.data
      })
    )
  }

  private _setCurrentBaseView(pipeline_view_id: string | null) {
    let pipelineView = this._baseViews.find((view: PipelineView) => view.id === pipeline_view_id) ?? null;
    this._currentBaseView = pipelineView;
    this.currentBaseView.next(pipelineView);
  }
  public savePipelineFilter(data:any){
    return this.apollo.mutate({
      mutation: save_pipeline_filter,
      variables: {
        name: data.name,
        pipeline_view_type_id: data.pipeline_view_type_id,
        pipeline_view_id: data.pipeline_view_id,
        group: JSON.stringify(data.group),
        sort: JSON.stringify(data.sort),
        filter: JSON.stringify(data.filter),
        person_id: data.person_id,
        group_id: data.group_id,
        associated_sponsor_id: data.sponsor_id,
        associated_organization_id: data.organization_id
      }
    })
  }

  public deletePipelineFilter(id:string){
    return this.apollo.mutate({
      mutation: delete_pipeline_filter,
      variables: {id}
    })
  }

  public updatePipelineFilter(data:any){
    return this.apollo.mutate({
      mutation: update_pipeline_filter,
      variables: {
        id: data.id,
        name: data.name,
        pipeline_view_id: data.pipeline_view_id,
        group: JSON.stringify(data.group),
        sort: JSON.stringify(data.sort),
        filter: JSON.stringify(data.filter),
        person_id: data.person_id,
        group_id: data.group_id,
        associated_sponsor_id: data.sponsor_id,
        associated_organization_id: data.organization_id
      }
    })
  }

  public getSubSelections(condition: FormGroup):any[]{
    let subList:any[] = []
    let fieldValue = condition.get('fieldValue') as FormControl
    let field = condition.get('field') as FormControl
    let model = field?.value?.['model']
    const source = this._rawDataByModel.get(model) ?? this.rawData
    const selectedValue:any = fieldValue?.value
    if(!selectedValue){
      return []
    }
    let listSelection:any = source.find((data:any) => data.id === selectedValue['id'])
    switch (field?.value?.['model']) {
      case 'AttributeType': {
        subList = listSelection ? [...listSelection.attributes] : []
      }
      break;
      case 'State': {
        subList = listSelection ? [...listSelection.counties] : []
      }
      break;
      default: subList = [...this.rawData]
    }
    return subList.sort((a: any, b: any) =>
      (a?.name ?? '').localeCompare(b?.name ?? ''));
  }

  private _formatModelSelectionData(data: any[], model: string, field?: string | null) {
    let formattedData: ModelSelection[] = [];
    let index = 0;

    const pushSelection = (id: string, name: string, idx: number): void => {
      formattedData.push({ id, name, index: idx });
    };

    switch (model) {
      case 'StatusSequence': {
        const fieldNameMap: Record<string, string> = {
          index: 'Closing',
          syndications_index: 'Syndications',
        };
        const targetName = field ? fieldNameMap[field] : undefined;
        if (targetName) {
          data.forEach((item: StatusSequence) => {
            if (item.name === targetName) {
              item.status_sequence_details?.forEach((detail: StatusSequenceDetail) => {
                pushSelection(detail.status?.id ?? '', detail.status?.name ?? '', detail.index);
              });
            }
          });
        }
        break;
      }

      case 'Subsequence':
        data.forEach((sequence: any) => {
          if (sequence.name === 'Pricing' && field === 'pricing_status') {
            sequence.substatuses.forEach((status: any) => {
              index += 1;
              pushSelection(status.id, status.name, status.index ?? index);
            });
          }
        });
        break;

      case 'State':
        data.forEach((state: State) => {
          index += 1;
          pushSelection(state?.id ?? '', state.abbreviation, index);
        });
        break;

      case 'RiskRating':
        data.forEach((rating: any) => {
          index += 1;
          pushSelection(rating.risk_rating, rating.risk_rating, index);
        });
        break;

      case 'ProjectCreditOutlet':
        data.forEach((pco: ProjectCreditOutlet) => {
          index += 1;
          pushSelection(
            pco.certificatedOutlet ? pco.certificatedOutlet.id : pco.fund ? pco.fund.id : '',
            pco.certificatedOutlet ? pco.certificatedOutlet.organization.name : pco.fund ? pco.fund.name : '',
            index
          );
        });
        break;

      case 'TaxCreditType':
        data.forEach((cr: TaxCreditType) => {
          index += 1;
          pushSelection(cr.id, cr.level + ' ' + cr.name, index);
        });
        break;

      default:
        // TaxCreditDesignation, DateClass, MetroStatArea, AttributeType,
        // HasSupportiveServices, HasUnitSetAsides, IsAvailable, Milestone,
        // LoanType, DateType, Lender, Sponsor, FundType, Role, and any future models.
        data.forEach((val: any) => {
          index += 1;
          pushSelection(val.id, val.name, val.index ?? index);
        });
        break;
    }

    /// Remove duplicated items (by id)
    const contains:Set<string> = new Set<string>()
    formattedData = formattedData.filter(item => {
      if(contains.has(item.id)) return false;
      contains.add(item.id);
      return true;
    })

    return formattedData.sort((a: ModelSelection, b: ModelSelection) => {
      return a.name.localeCompare(b.name);
    });
  }

  public getModelSelections(condition: FormGroup, control: string = 'field'): any[] {
    const model = condition.get(control)?.value?.model
    const fresh = model ? (this._modelSelections.get(model) ?? []) : []
    if(fresh.length){
      this._conditionSelections.set(condition, fresh)
      return fresh;
    }
    return this._conditionSelections.get(condition) ?? []
  }

  public getTaggedParticipantsByProject(id:string){
    return this.apollo.query({
      query: tagged_participants_by_project,
      variables: {id},
    }).pipe(
      map((response: any) => {
        let data = response.data.project.taggedParticipants
        let validTags = data.flatMap((d:Participant) => {
          const participant: Person | Organization | null = d.person || d.organization || null
          return participant?.entity.entity_types.filter((ent: any) => ent.type.tagType.name==='descriptive') ?? []
        })
        let tags:string[] = []
        validTags.forEach((tag:any) => tags.push(tag.type.name))
        return tags
      })
    )
  }

  public callSingleParticipantRow(id:string){
    return this.apollo.query({
      query: call_single_participant,
      variables: {id}
    })
  }

  public getSortSelections(condition: FormGroup, control: string = 'field'): any[] {
    const model = condition.get(control)?.value?.model
    return model ? (this._sortingSelections.get(model) ?? []) : []
  }

  public splitExpandedRowChunk<T>(arr: T[], size: number): T[][] {
    const result: T[][] = [];
    for (let i = 0; i < arr.length; i += size) {
      result.push(arr.slice(i, i + size));
    }
    return result;
  }

  public deletePipelinePermission(id: string){
    return this.apollo.mutate({
      mutation: delete_permission,
      variables: {id}
    })
  }



  public resetSelections(){
    this._modelSelections.clear()
    this.modelSelections.next(this._modelSelections)
  }

}
